import { Suspense, lazy } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router'

import { Layout } from '@/components/Layout'
import { AboutPage } from '@/pages/AboutPage'
import { ArtworkPage } from '@/pages/ArtworkPage'
import { ContactsPage } from '@/pages/ContactsPage'
import { GalleryPage } from '@/pages/GalleryPage'
import { HomePage } from '@/pages/HomePage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { detectLocale } from '@/i18n'
import { DEFAULT_LOCALE, isLocale } from '@/lib/types'

/**
 * The admin panel is loaded on demand.
 *
 * A visitor looking at paintings should never download the drag-and-drop
 * library, the upload forms or the user management screens — that is most of
 * the JavaScript in this project and none of it is theirs.
 */
const AdminApp = lazy(() =>
  import('@/admin/AdminApp').then((module) => ({ default: module.AdminApp }))
)

export function App() {
  return (
    <Routes>
      {/* The admin panel has no language prefix and must be matched before
          the /:locale route, which would otherwise swallow "/admin". */}
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<AdminLoading />}>
            <AdminApp />
          </Suspense>
        }
      />

      <Route path="/:locale" element={<LocaleGuard />}>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="gallery/:category" element={<GalleryPage />} />
          <Route path="work/:ref" element={<ArtworkPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>

      {/* Anything without a language prefix, including the bare domain. */}
      <Route path="*" element={<RedirectToLocale />} />
    </Routes>
  )
}

/**
 * Rejects made-up language codes.
 *
 * Without this, /fr/gallery would render the Ukrainian site at a URL that
 * search engines would then index as a separate page — duplicate content at an
 * address that should not exist.
 */
function LocaleGuard() {
  const { locale } = useParams()
  const location = useLocation()

  if (!isLocale(locale)) {
    const rest = location.pathname.split('/').slice(2).join('/')
    return <Navigate to={`/${DEFAULT_LOCALE}${rest ? `/${rest}` : ''}`} replace />
  }
  return <Outlet />
}

/** Sends a visitor at the bare domain to their most likely language. */
function RedirectToLocale() {
  const location = useLocation()
  const locale = typeof document === 'undefined' ? DEFAULT_LOCALE : detectLocale()
  const suffix = location.pathname === '/' ? '' : location.pathname
  return <Navigate to={`/${locale}${suffix}`} replace />
}

function AdminLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-stone-300 border-t-stone-900" />
    </div>
  )
}
